// React hooks
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers i API servisi
import { useTranslate } from "helpers";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { InvoiceIncomingRejection, InvoiceIncomingRow } from "services/api/finance/incoming-invoices/types";

// API
import {
  getInvoiceIncomingsAPI,
  deleteInvoiceIncomingAPI,
  approveInvoiceIncomingAPI,
  rejectInvoiceIncomingAPI,
  markPaidInvoiceIncomingAPI,
} from "services/api/finance/incoming-invoices/api";

// UI komponente
import { NoResults } from "ui/components/NoResults";
import { Button } from "ui/components/Button/Button";
import { TableSearch } from "ui/components/Table/TableSearch";
import { Dialog } from "ui/components/Dialog";

// Lokalne komponente
import InvoiceIncomingPreviewCard from "ui/modules/finance/pages/invoices/InvoiceSearchPage/components/InvoiceIncomingPreviewCard/InvoiceIncomingPreviewCard";
import InvoiceIncomingEditCard from "ui/modules/finance/pages/invoices/InvoiceIncomingEditCard/InvoiceIncomingEditCard";
import InvoiceIncomingRejectModal from "ui/modules/finance/pages/invoices/InvoiceSearchPage/components/InvoiceIncomingRejectModal/InvoiceIncomingRejectModal";
import { VEHICLE_INCOMING_INVOICE_SEARCH_COLUMNS } from "./consts";


const VehicleInvoiceIncomingSearchTab: React.FC = () => {
  const { id: vehicleId } = useParams();
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<InvoiceIncomingRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [invoiceId, setInvoiceId] = useState<string>("");
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editInvoiceId, setEditInvoiceId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectInvoiceNumber, setRejectInvoiceNumber] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, [vehicleId]);

  const fetchSearchResult = async (pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getInvoiceIncomingsAPI(pagination, { vehicleId });
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    // Ručno uneseni računi se otvaraju na uređivanje, e-računi ostaju samo za pregled.
    const row = searchResults.data.find((invoice) => invoice.id === id);

    if (row?.incomingSourceId === "manual") {
      setEditInvoiceId(id);
      setShowEditModal(true);
      return;
    }

    setInvoiceId(id);
    setShowAddNewModal(true);
  }, [searchResults.data]);

  const handleAddNew = useCallback(() => {
    setEditInvoiceId(null);
    setShowEditModal(true);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setShowEditModal(false);
    setEditInvoiceId(null);
  }, []);

  const handleEditModalSuccess = useCallback(() => {
    setShowEditModal(false);
    setEditInvoiceId(null);
    fetchSearchResult(searchResults.pagination);
  }, [searchResults.pagination]);

  const handlePreviewCardClose = useCallback(() => {
    setShowAddNewModal(false);
  }, []);

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setInvoiceId("");
    setShowAddNewModal(false);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setInvoiceId(id);
    setOpenDialog(true);
  };

  const handleApproveClick = useCallback(async (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await approveInvoiceIncomingAPI(id, t);
      fetchSearchResult(searchResults.pagination);
    } catch { }
  }, [t]);

  const handleRejectClick = useCallback(async (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    // Odbijanje e-računa se fiskalizira kod Porezne, pa razlog tražimo prije slanja.
    const row = searchResults.data.find((invoice) => invoice.id === id);
    if (row?.incomingSourceId === "provider") {
      setRejectId(id);
      setRejectInvoiceNumber(row.invoiceNumber ?? null);
      return;
    }

    try {
      await rejectInvoiceIncomingAPI(id, null, t);
      fetchSearchResult(searchResults.pagination);
    } catch { }
  }, [searchResults.data, t]);

  const handleRejectModalHide = useCallback(() => {
    setRejectId(null);
    setRejectInvoiceNumber(null);
  }, []);

  const handleRejectConfirm = useCallback(async (rejection: InvoiceIncomingRejection) => {
    if (!rejectId) return;

    try {
      await rejectInvoiceIncomingAPI(rejectId, rejection, t);
      setRejectId(null);
      setRejectInvoiceNumber(null);
      fetchSearchResult(searchResults.pagination);
    } catch { }
  }, [rejectId, searchResults.pagination, t]);

  const handleMarkPaidClick = useCallback(async (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await markPaidInvoiceIncomingAPI(id, t);
      fetchSearchResult(searchResults.pagination);
    } catch { }
  }, [t]);

  const handleInvoiceDeleteDialogConfirm = useCallback(async () => {
    if (!invoiceId) return;
    try {
      await deleteInvoiceIncomingAPI(invoiceId, t);
      fetchSearchResult(searchResults.pagination);
      setOpenDialog(false);
      setInvoiceId("");
    } catch { }
  }, [invoiceId, t]);

  const handleInvoiceDeleteDialogClose = useCallback(() => {
    setInvoiceId("");
    setOpenDialog(false);
  }, []);

  return (
    <>
        <div className="px-0 py-6 mx-[-1px] mt-[-1px] xl:px-6 border">
        <div className="flex justify-between items-center">
          <div />
          <div className="py-3 flex justify-end">
            <Button
              variant="primary"
              children="Dodaj račun"
              startIcon="/media/icons/plus.svg"
              onClick={handleAddNew}
            />
          </div>
        </div>

        {!isLoading && searchResults.pagination.totalPages === 0 ? (
          <NoResults message="Nema ulaznih računa za ovo vozilo" />
        ) : (
          <TableSearch
            columns={VEHICLE_INCOMING_INVOICE_SEARCH_COLUMNS(
              t,
              handleApproveClick,
              handleRejectClick,
              handleMarkPaidClick,
              handleDeleteClick
            )}
            data={searchResults.data}
            selectedRows={selectedRows}
            pagination={
              searchResults.pagination || initialDataWithPagination.pagination
            }
            selectableRows={false}
            onPaginationChange={handlePaginationChange}
            onSelectedChange={handleSelectedChange}
            isLoading={isLoading}
            onRowClick={handleRowClick}
          />
        )}
      </div>

      <Dialog
        open={openDialog}
        title="Brisanje ulaznog računa"
        message="Jeste li sigurni da želite obrisati ovaj ulazni račun?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleInvoiceDeleteDialogConfirm}
        onCancel={handleInvoiceDeleteDialogClose}
      />

      <InvoiceIncomingRejectModal
        show={!!rejectId}
        onHide={handleRejectModalHide}
        onConfirm={handleRejectConfirm}
        invoiceNumber={rejectInvoiceNumber}
      />

      <InvoiceIncomingPreviewCard
        show={showAddNewModal}
        invoiceId={invoiceId || null}
        onSuccess={handleEditSuccess}
        onClose={handlePreviewCardClose}
      />

      <InvoiceIncomingEditCard
        show={showEditModal}
        invoiceId={editInvoiceId}
        vehicleId={vehicleId ?? null}
        onSuccess={handleEditModalSuccess}
        onClose={handleEditModalClose}
      />
    </>
  );
};

export default VehicleInvoiceIncomingSearchTab;
